import React from "react";
import {
	Avatar,
	Box,
	Chip,
	IconButton,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import EmailTwoToneIcon from "@mui/icons-material/EmailTwoTone";
import PhoneIphoneTwoToneIcon from "@mui/icons-material/PhoneIphoneTwoTone";
import moment from "moment";
import type { IVisitorListItem } from "../type/newVisitorType";

interface Props {
	visitor: IVisitorListItem;
	index: number;
	paginationSkip: number | string;
	onView: (id: number) => void;
	onEdit: (id: number) => void;
	onDelete: (id: number, name: string) => void;
}

const getStatusChip = (status: string) => {
	const normalized = (status || "").toLowerCase();
	if (normalized === "approved" || normalized === "paid") {
		return <Chip label={status || "Approved"} color="success" size="small" variant="filled" />;
	}
	if (normalized.includes("pending")) {
		return <Chip label={status || "Pending"} color="warning" size="small" variant="filled" />;
	}
	if (normalized === "rejected" || normalized === "unpaid") {
		return <Chip label={status || "Rejected"} color="error" size="small" variant="filled" />;
	}
	return <Chip label={status || "Active"} color="default" size="small" variant="outlined" />;
};

const VisitorTableRow: React.FC<Props> = ({
	visitor,
	index,
	paginationSkip,
	onView,
	onEdit,
	onDelete,
}) => {
	const theme = useTheme();
	const fullName =
		`${visitor.visitor_name || ""}`.trim() ||
		"Unnamed Visitor";

	return (
		<TableRow hover key={visitor.visitor_id}>
			{/* SL */}
			<TableCell>
				<Typography variant="body2" color="text.secondary">
					{index + 1 + Number(paginationSkip)}
				</Typography>
			</TableCell>

			{/* Visitor No */}
			<TableCell>
				<Chip
					label={visitor.visitor_no || `#${visitor.visitor_id}`}
					size="small"
					color="primary"
					variant="outlined"
					sx={{ fontWeight: 600 }}
				/>
			</TableCell>

			{/* Visitor Name */}
			<TableCell>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
					<Box>
						<Typography
							variant="body1"
							color="text.primary"
							fontWeight={600}
							sx={{
								cursor: "pointer",
								"&:hover": { color: "primary.main" },
							}}
							onClick={() => onView(visitor.visitor_id)}
						>
							{fullName}
						</Typography>

					</Box>
				</Box>
			</TableCell>

			{/* Contact Information */}
			<TableCell>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					{visitor.visitor_email && (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<EmailTwoToneIcon sx={{ fontSize: 14, color: "text.secondary" }} />
							<Typography variant="body2" color="text.secondary">
								{visitor.visitor_email}
							</Typography>
						</Box>
					)}
					{visitor.visitor_phone && (
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<PhoneIphoneTwoToneIcon sx={{ fontSize: 14, color: "text.secondary" }} />
							<Typography variant="body2" color="text.secondary">
								{visitor.visitor_phone}
							</Typography>
						</Box>
					)}
				</Box>
			</TableCell>



			{/* Status */}
			<TableCell>{getStatusChip(visitor.visitor_status)}</TableCell>

			{/* Created At */}
			<TableCell>
				<Typography variant="body2" color="text.secondary">
					{visitor.visitor_created_at
						? moment(visitor.visitor_created_at).format("ll")
						: "-"}
				</Typography>
			</TableCell>

			{/* Actions */}
			<TableCell align="right">
				<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
					{/* View */}
					<Tooltip title="View Details" arrow>
						<IconButton
							onClick={() => onView(visitor.visitor_id)}
							sx={{
								"&:hover": {
									background:
										theme.colors?.primary?.lighter || "rgba(85, 105, 255, 0.1)",
								},
								color: theme.palette.primary.main,
							}}
							size="small"
						>
							<VisibilityIcon fontSize="small" />
						</IconButton>
					</Tooltip>

					{/* Edit */}
					<Tooltip title="Edit Visitor" arrow>
						<IconButton
							onClick={() => onEdit(visitor.visitor_id)}
							sx={{
								"&:hover": {
									background:
										theme.colors?.primary?.lighter || "rgba(85, 105, 255, 0.1)",
								},
								color: theme.palette.primary.main,
							}}
							size="small"
						>
							<EditTwoToneIcon fontSize="small" />
						</IconButton>
					</Tooltip>

					{/* Delete */}
					{/* <Tooltip title="Delete Visitor" arrow>
						<IconButton
							onClick={() => onDelete(visitor.visitor_id, fullName)}
							sx={{
								"&:hover": {
									background:
										theme.colors?.error?.lighter || "rgba(255, 25, 67, 0.1)",
								},
								color: theme.palette.error.main,
							}}
							size="small"
						>
							<DeleteTwoToneIcon fontSize="small" />
						</IconButton>
					</Tooltip> */}
				</Box>
			</TableCell>
		</TableRow>
	);
};

export default VisitorTableRow;
